import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

// Icons
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";        // Studio sound
import FormatClearRoundedIcon from "@mui/icons-material/FormatClearRounded";     // Remove filler words
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";           // Shorten word gaps
import ContentCutRoundedIcon from "@mui/icons-material/ContentCutRounded";       // Create clips
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PeopleIcon from "@mui/icons-material/People";
import {BookmarkBorderOutlined, BookmarksOutlined} from "@mui/icons-material";     // Title icon (sparkle)
import { FillerWordStore } from "./FillerWords";
import { GapStore } from "./Gaps";
import { useAssetStore } from "../../storage/AssetStore";
import { CompositionService } from "../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../librettoEditor/store/ScriptLayerStore";
import { observer } from "mobx-react-lite";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';

const titleTextStyle: React.CSSProperties = {
    color: "var(--app-text-primary)",
    paddingLeft: "8px",
    paddingRight: "8px",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    fontFamily: "Inter, sans-serif",
    userSelect: "none",
};

// Shared paper shell
const PaperPanel: React.FC<{ title: string; children: React.ReactNode; fill?: boolean }> = ({
                                                                                                title,
                                                                                                children,
                                                                                                fill,
                                                                                            }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            background: "var(--app-bg-surface)",
            border: "0.5px solid var(--app-border-subtle)",
            borderRadius: "12px",
            px: 1,
            py: 1,
            ...(fill ? { flex: 1, minHeight: 0 } : {}),
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, py: 0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--app-text-primary)" }}>{title}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ ...(fill ? { overflow: "auto" } : {}) }}>{children}</Box>
    </Box>
);

// Reusable row
const ToolRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    disabledReason?: string;
    dataTour?: string;
}> = ({ icon, label, onClick, disabled, disabledReason, dataTour }) => {
    const isDisabled = Boolean(disabled) || !onClick;

    const row = (
        <ListItem
            disablePadding
            sx={{
                ...(isDisabled ? {} : { "&:hover": { background: "var(--app-bg-subtle)", borderRadius: "8px" } }),
                "& .chev": { opacity: 0, transition: "opacity .15s" },
                ...(isDisabled ? { "&:hover .chev": { opacity: 0 } } : { "&:hover .chev": { opacity: 1 } }),
                alignItems: "center",
            }}
            secondaryAction={
                isDisabled ? undefined : (
                    <ChevronRightRoundedIcon className="chev" sx={{ color: "var(--app-text-muted)", transform: "translateY(1px)" }} />
                )
            }
        >
            <ListItemButton
                data-tour={dataTour}
                disabled={isDisabled}
                onClick={isDisabled ? undefined : onClick}
                sx={{
                    py: 1.0,
                    pr: 4,
                    ...(isDisabled
                        ? {
                              cursor: "not-allowed",
                              opacity: 0.5,
                              "& .MuiListItemIcon-root": { opacity: 0.7 },
                          }
                        : {}),
                }}
            >
                <ListItemIcon sx={{ minWidth: 28, mr: 1.5 }}>{icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, color: "var(--app-text-primary)" }} />
            </ListItemButton>
        </ListItem>
    );

    if (!isDisabled || !disabledReason) return row;

    return (
        <Tooltip title={disabledReason} placement="right">
            {row}
        </Tooltip>
    );
};

const V2AIToolsSidebar: React.FC<{
    onAddChapters?: () => void;
    onAutomaticMulticam?: () => void;
    onRemoveRetakes?: () => void;
    onDecomposeClip?: () => void;
    decomposeDisabledReason?: string | null;
}> = observer(({
    onAddChapters,
    onAutomaticMulticam,
    onRemoveRetakes,
    onDecomposeClip,
    decomposeDisabledReason,
}) => {
    const { GetTranscript } = useAssetStore();

    const scriptClip: any = CompositionService.getScriptLayerClip?.();
    const videoSourcesCount = Number(
        typeof scriptClip?.getVideoEntries === "function" ? scriptClip.getVideoEntries().length : 0,
    );

    // Speaker count heuristic:
    // - Prefer explicit speaker overrides (sequence-created scripts collapse to 1 speaker per source).
    // - Fall back to diarization speakerIds in the loaded transcript words.
    const overrideSpeakers = new Set(
        (ScriptLayerStore.speakerOverrides ?? [])
            .map((o: any) => String(o?.speakerId ?? o?.speakerName ?? o?.id ?? ""))
            .filter(Boolean),
    );
    const diarizationSpeakers = new Set<string>();
    try {
        for (const item of ScriptLayerStore.items ?? []) {
            for (const w of (item as any)?.words ?? []) {
                const id = (w as any)?.speakerId;
                if (id == null) continue;
                diarizationSpeakers.add(String(id));
                if (diarizationSpeakers.size >= 6) break;
            }
            if (diarizationSpeakers.size >= 6) break;
        }
    } catch {}
    const speakerCount = overrideSpeakers.size >= 2 ? overrideSpeakers.size : diarizationSpeakers.size;
    const isAutomaticMulticamEnabled = videoSourcesCount >= 2 && speakerCount >= 2;

    const hasTranscriptContent = (() => {
        try {
            for (const item of ScriptLayerStore.items ?? []) {
                const words = (item as any)?.words;
                if (Array.isArray(words) && words.length > 0) return true;
            }
        } catch {}
        return false;
    })();
	    
    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                p: "3px",
            }}
        >
            {/* Title (keeps your unified header pattern) */}
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", marginLeft: "8px" }}>
                <Box sx={{ width: "24px", height: "24px", display: "grid", placeItems: "center" }}>
                    <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: "var(--app-icon-primary)" }} />
                </Box>
                <Box sx={titleTextStyle}>AI Tools</Box>
            </Box>

            <PaperPanel title="AI Tools" fill>
                <List dense sx={{ py: 0 }}>
                    {/*<ToolRow*/}
                    {/*    icon={<AutoFixHighOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />}*/}
                    {/*    label="Edit for clarity"*/}
                    {/*    onClick={*/}
                    {/*        hasTranscriptContent*/}
                    {/*            ? () => {*/}
                    {/*                // Fetch words from script clip and activate filler word mode*/}
                    {/*                console.log("Calling filler word store activate async");*/}
                    {/*                FillerWordStore.activateAsync(GetTranscript);*/}
                    {/*            }*/}
                    {/*            : undefined*/}
                    {/*    }*/}
                    {/*/>*/}
	                    <ToolRow
	                        icon={<FormatClearRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />}
	                        label="Remove filler words"
                            dataTour="ai-tools-remove-filler"
	                        onClick={
	                            hasTranscriptContent
	                                ? () => {
	                                      // Fetch words from script clip and activate filler word mode
	                                      console.log("Calling filler word store activate async");
	                                      FillerWordStore.activateAsync(GetTranscript);
	                                  }
	                                : undefined
	                        }
	                    />
	                    {/*<ToolRow*/}
	                    {/*    icon={<RepeatOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />}*/}
	                    {/*    label="Remove retakes"*/}
	                    {/*    onClick={hasTranscriptContent && onRemoveRetakes ? () => onRemoveRetakes() : undefined}*/}
	                    {/*/>*/}
	                    <ToolRow
	                        icon={<CompressRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />}
	                        label="Shorten or remove pauses"
	                        onClick={
	                            hasTranscriptContent
	                                ? () => {
	                                      // Fetch words from backend and activate gap mode
	                                      console.log("Calling gap store activateAsync");
	                                      GapStore.activateAsync(GetTranscript);
	                                  }
	                                : undefined
	                        }
	                    />
	                    <ToolRow
	                        icon={<BookmarkBorderOutlined sx={{ fontSize: 18, color: "#6B7280" }} />}
	                        label="Add chapters"
	                        onClick={hasTranscriptContent && onAddChapters ? () => onAddChapters() : undefined}
	                    />
                        <ToolRow
                            icon={<PeopleIcon sx={{ fontSize: 18, color: "#6B7280" }} />}
                            label="Separate speakers"
                            dataTour="ai-tools-separate-speakers"
                            onClick={
                                onDecomposeClip && !decomposeDisabledReason
                                    ? () => onDecomposeClip()
                                    : undefined
                            }
                            disabled={Boolean(decomposeDisabledReason)}
                            disabledReason={decomposeDisabledReason ?? undefined}
                        />
	                    {/*<ToolRow*/}
	                    {/*    icon={<PeopleOutlineIcon sx={{ fontSize: 18, color: "#6B7280" }} />}*/}
	                    {/*    label="Automatic multicam"*/}
	                    {/*    onClick={*/}
                        {/*          hasTranscriptContent && isAutomaticMulticamEnabled && onAutomaticMulticam*/}
	                    {/*            ? () => onAutomaticMulticam()*/}
	                    {/*            : undefined*/}
	                    {/*    }*/}
	                    {/*/>*/}
                    {/*<ToolRow*/}
                    {/*    icon={<ContentCutRoundedIcon sx={{ fontSize: 18, color: "#374151" }} />}*/}
                    {/*    label="Create clips"*/}
                    {/*    onClick={() => {*/}
                    {/*        // TODO: auto-find highlights and create clips*/}
                    {/*    }}*/}
                    {/*/>*/}
                    {/*<ToolRow*/}
                    {/*    icon={<BookmarksOutlined sx={{ fontSize: 18, color: "#374151" }} />}*/}
                    {/*    label="Add Chapters"*/}
                    {/*    onClick={() => {*/}
                    {/*        // TODO: auto-find highlights and create clips*/}
                    {/*    }}*/}
                    {/*/>*/}
                </List>
            </PaperPanel>
        </Box>
    );
});

export default V2AIToolsSidebar;
